import { Button } from "@crew/ui/components/button";
import { Spinner } from "@crew/ui/components/spinner";
import { useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { afterSignIn, redirectFrom } from "../features/auth/next";
import { lingerRedirect, redirectFor } from "../features/setup/onboarding";
import { errorHint } from "./api";
import { useMe, type Me } from "./me";
import { HOME } from "./home";

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-6 text-muted-foreground">
      {children}
    </div>
  );
}

/** `/api/me` hasn't settled: a spinner, or why it failed with Try again. */
function MeNotReady({ error, refetch }: { error: Error | null; refetch: () => unknown }) {
  if (!error) {
    return (
      <Centered>
        <Spinner className="size-5" />
      </Centered>
    );
  }
  return (
    <Centered>
      <p>
        {errorHint(error)}{" "}
        <Button variant="link" onClick={() => void refetch()}>
          Try again
        </Button>
      </p>
    </Centered>
  );
}

/**
 * Renders `children(me)` once `/api/me` settles and the person is where they
 * belong (`redirectFor`): signed out → /login, unconfirmed → /verify, unpaid → /checkout,
 * onboarding → /setup, else the shell. With `linger`, a page already shown
 * isn't sent home: it goes there itself (`/setup` says it's done first).
 */
export function RequireMe({
  need,
  linger = false,
  children,
}: {
  need: "active" | "pending" | "verify" | "setup";
  linger?: boolean;
  children: (me: Me) => ReactNode;
}) {
  const query = useMe();
  const location = useLocation();
  const [shown, setShown] = useState(false);
  if (!query.isSuccess) return <MeNotReady error={query.error} refetch={query.refetch} />;
  const to = lingerRedirect(redirectFor(query.data, need), linger, shown);
  // Signed out, the page goes with them to sign-in, to come back to.
  if (to) return <Navigate to={redirectFrom(to, location)} replace />;
  if (!shown) setShown(true);
  // No redirect means signed in.
  return <>{children(query.data as Me)}</>;
}

/** Login and signup: a signed-in person goes straight in, to `?next=` if it names a page. */
export function RequireSignedOut({ children }: { children: ReactNode }) {
  const { data: me, isPending } = useMe();
  const { search } = useLocation();
  if (isPending) {
    return (
      <Centered>
        <Spinner className="size-5" />
      </Centered>
    );
  }
  if (me) {
    const to = me.workspace.status === "active" ? afterSignIn(search, HOME) : "/checkout";
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}
